/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useEffect, useRef, useState } from "react";
import { useAuth } from "./useAuth";
import useWidgetCallbackParams from "./useWidgetCallbackParams";
import { useTokenStorage } from "./useTokenStorage";

export const useAuthTokenInitialization = () => {
  const { setIsInitialized, isInitialized, loginByHash } = useAuth();
  const { hash, isRestorePassword, isOnlineMeeting, clearParams } = useWidgetCallbackParams();
  const { token, source, setToken } = useTokenStorage();
  const hasInitialized = useRef(false);
  const [initError, setInitError] = useState(null);

  if (initError) {
    throw initError;
  }

  useEffect(() => {
    const runInitialization = async () => {
      if (hasInitialized.current) {
        return;
      }

      if (isRestorePassword || isOnlineMeeting) {
        hasInitialized.current = true;
        setIsInitialized(true);

        return;
      }

      if (isInitialized) {
        hasInitialized.current = true;

        return;
      }

      if (hash) {
        hasInitialized.current = true;
        try {
          await loginByHash({ hash });
        } catch (error) {
          clearParams();
          setInitError(error);
          return;
        }
        setIsInitialized(true);

        return;
      }

      if (token) {
        hasInitialized.current = true;
        setToken({
          token,
          rememberMe: source === "localStorage",
          isAnonymous: false,
        });
        setIsInitialized(true);

        return;
      }

      hasInitialized.current = true;
      setIsInitialized(true);
    };

    runInitialization();
  }, [hash, token, isInitialized, isRestorePassword, isOnlineMeeting, loginByHash, setToken, source, setIsInitialized, clearParams]);

  return {
    isInitialized,
  };
};
